import { Form, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Col, Row, Select, TablePaginationConfig } from 'antd/lib';
import { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import growingDegreeDayApi from '../../../apis/GrowingDegreeDayApi';
import ConstantLabel from '../../../components/ConstantLabel/ConstantLabel';
import CustomContext from '../../../contexts/CustomContext';
import { Page } from '../../../models/Elements';
import { AemetWeatherStation, CustomAuth, GrowingDegreeDay } from '../../../models/Entities';
import { PhaseType, phaseTypes, plagues, PlagueType } from '../../../models/Types';
import alertService from '../../../services/AlertService';
import stringService from '../../../services/StringService';
import paginationService from '../../../services/TableService';
import styles from './GrowingDegreeDayComponent.module.scss';

/**
 * Returns the growing degree day component.
 * @returns the growing degree day component.
 */
const GrowingDegreeDayComponent: React.FC<Props> = (props) => {
    /*** HOOKS ***/

    const { aemetWeatherStations, aemetWeatherStationId } = props;
    const intl = useIntl();
    const [form] = Form.useForm();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;
    const [gdasPage, setGdasPage] = useState<Page<GrowingDegreeDay>>();
    const [page, setPage] = useState<number>(0);
    const [filter, setFilter] = useState<Filter>({});
    const [sortOrder, setSortOrder] = useState<boolean>(false);
    const [sortField, setSortField] = useState<string>('measurementDate');
    const [loading, setLoading] = useState<'loading'>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                if (aemetWeatherStationId) {
                    const gdasPage = await growingDegreeDayApi.list(
                        page,
                        paginationService.pageSize,
                        sortField,
                        sortOrder,
                        aemetWeatherStationId,
                        filter.plague,
                        filter.generation,
                        filter.phase
                    );
                    setGdasPage(gdasPage);
                }
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [auth.companyId, aemetWeatherStationId, intl, page, sortOrder, sortField, filter.plague, filter.generation, filter.phase]);

    /*** METHODS ***/

    const selectedAemetStation = aemetWeatherStations.find((i) => i.aemetWeatherStationId === aemetWeatherStationId);

    const handleTableChange = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        setPage(pagination.current ? pagination.current - 1 : 0);
        sorter.field !== undefined && setSortField(sorter.field);
        setSortOrder(sorter.order === 'ascend');
    };

    const filterGdas = async (values: any) => {
        const filter: Filter = {
            plague: values.plague,
            generation: values.generation,
            phase: values.phase
        };
        setFilter(filter);
        setPage(0);
    };

    /*** VISUAL ***/
    const plagueOptions = plagues.map((plague) => (
        <Select.Option key={plague} value={plague}>
            <ConstantLabel value={plague} prefix="plague.type." />
        </Select.Option>
    ));
    const phaseOptions = phaseTypes.map((p) => (
        <Select.Option key={p} value={p}>
            <ConstantLabel value={p} prefix="gda.phase." />
        </Select.Option>
    ));

    const generations = [];
    for (let i = 1; i <= 10; i++) {
        generations.push(i);
    }

    const generationOptions = generations.map((i) => (
        <Select.Option key={i} value={i}>
            {i}
        </Select.Option>
    ));

    const items = gdasPage ? gdasPage.content : [];
    const columns: ColumnsType<GrowingDegreeDay> = [
        {
            title: <FormattedMessage id="gda.measurementDate" />,
            dataIndex: 'measurementDate',
            key: 'measurementDate',
            width: 120,
            align: 'center',
            sorter: true,
            defaultSortOrder: 'descend',
            render: (value: Dayjs, gda: GrowingDegreeDay) => value && <FormattedDate value={value as any} year="numeric" month="2-digit" day="2-digit" />
        },
        {
            title: <FormattedMessage id="gda.aemetWeatherStationId" />,
            dataIndex: 'aemetWeatherStationId',
            key: 'aemetWeatherStationId',
            render: (value: string, gda: GrowingDegreeDay) => selectedAemetStation?.name && selectedAemetStation.name
        },
        {
            title: <FormattedMessage id="gda.plague" />,
            dataIndex: 'plague',
            key: 'plague',
            align: 'center',
            width: 240,
            render: (value: string | undefined, gda: GrowingDegreeDay) => <ConstantLabel value={value} prefix="plague.type." />
        },
        {
            title: <FormattedMessage id="gda.result" />,
            dataIndex: 'result',
            key: 'result',
            align: 'right',
            width: 240,
            render: (value: number | undefined) => value !== undefined && <FormattedNumber value={value} minimumFractionDigits={1} maximumFractionDigits={1} />
        },
        {
            title: <FormattedMessage id="gda.generation" />,
            dataIndex: 'generation',
            key: 'generation',
            width: 240,
            align: 'right',
            render: (value: number | undefined, gda: GrowingDegreeDay) =>
                value !== undefined && <FormattedNumber value={value} minimumFractionDigits={0} maximumFractionDigits={0} />
        },
        {
            title: <FormattedMessage id="gda.phase" />,
            dataIndex: 'phase',
            key: 'phase',
            width: 240,
            align: 'center',
            render: (value: PhaseType | undefined, gda: GrowingDegreeDay) => <PhaseVariationComponent phase={value} />
        }
    ];

    return (
        <>
            <Form form={form} onFinish={filterGdas} colon={false} layout="vertical" requiredMark={false}>
                <Row gutter={[24, 24]}>
                    <Col span={6} lg={6}>
                        <Form.Item name="plague">
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="gda.plague" />}
                                className={styles.select}
                                onSelect={form.submit}
                                allowClear
                            >
                                {plagueOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={5} lg={3}>
                        <Form.Item name="generation">
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="gda.generation" />}
                                className={styles.select}
                                onSelect={form.submit}
                                allowClear
                            >
                                {generationOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={6} lg={6}>
                        <Form.Item name="phase">
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="gda.phase" />}
                                className={styles.select}
                                onSelect={form.submit}
                                allowClear
                            >
                                {phaseOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table
                dataSource={items}
                columns={columns}
                pagination={paginationService.createPagination(gdasPage!)}
                rowKey="id"
                onChange={handleTableChange}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading === 'loading'}
                className="table"
            />
        </>
    );
};
export default GrowingDegreeDayComponent;

interface Props {
    aemetWeatherStationId: string | undefined;
    aemetWeatherStations: AemetWeatherStation[];
}

interface Filter {
    plague?: PlagueType | undefined;
    generation?: number | undefined;
    phase?: PhaseType | undefined;
}

/**
 * Returns the tag corresponding to the growing degree day phase
 * @param props the props
 * @returns  the icon
 */
const PhaseVariationComponent: React.FC<PhaseVariationProps> = (props) => {
    const { phase } = props;

    switch (phase) {
        case 'L1_L3':
        case 'N1_N2':
            return (
                <Tag color="orange">
                    <ConstantLabel value={phase} prefix="gda.phase." />
                </Tag>
            );
        case 'L4_L6':
        case 'N1_N4':
            return (
                <Tag color="red">
                    <ConstantLabel value={phase} prefix="gda.phase." />
                </Tag>
            );
        case 'EGG':
            return (
                <Tag color="blue">
                    <ConstantLabel value={phase} prefix="gda.phase." />
                </Tag>
            );
        case 'PUPA':
            return (
                <Tag color="purple">
                    <ConstantLabel value={phase} prefix="gda.phase." />
                </Tag>
            );
        case 'ADULT':
            return (
                <Tag color="green">
                    <ConstantLabel value={phase} prefix="gda.phase." />
                </Tag>
            );
        default:
            return <></>;
    }
};
interface PhaseVariationProps {
    phase: PhaseType | undefined;
}
